import { uiText } from "@/lib/i18n/uiText";
import { Link } from "@/lib/router-compat";
import { ArrowRight, Shield } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { InternalLinks } from "@/components/seo/InternalLinks";
import { LegalDisclaimer } from "@/components/shared/LegalDisclaimer";
import { useSEO } from "@/lib/useSEO";
import { organizationJsonLd, breadcrumbJsonLd, webPageJsonLd } from "@/lib/seoSchema";
import { slugify } from "@/lib/entities";

const CANONICAL = "/clubs";

const crumbs = [
  { name: "Home", path: "/" },
  { name: "Clubs", path: CANONICAL },
];

/**
 * Clubs directory. `/clubs/` previously resolved to the 404 page (which sets
 * `noindex`), so every `/clubs/:slug` hub was an orphan with a non-indexable
 * parent. This page is the indexable breadcrumb parent and the crawl entry
 * point into every club hub.
 */
const CLUB_GROUPS: { league: string; leaguePath: string; clubs: string[] }[] = [
  {
    league: "Premier League",
    leaguePath: "/leagues/epl",
    clubs: [
      "Arsenal", "Aston Villa", "Chelsea", "Everton", "Liverpool",
      "Manchester City", "Manchester United", "Newcastle United",
      "Tottenham Hotspur", "West Ham United",
    ],
  },
  {
    league: "La Liga",
    leaguePath: "/leagues/la-liga",
    clubs: ["Athletic Club", "Atletico Madrid", "Barcelona", "Real Betis", "Real Madrid", "Real Sociedad", "Sevilla", "Valencia"],
  },
  {
    league: "Serie A",
    leaguePath: "/leagues/serie-a",
    clubs: ["AC Milan", "Atalanta", "Inter Milan", "Juventus", "Lazio", "Napoli", "Roma", "Fiorentina"],
  },
  {
    league: "Bundesliga",
    leaguePath: "/leagues/bundesliga",
    clubs: ["Bayer Leverkusen", "Bayern Munich", "Borussia Dortmund", "Eintracht Frankfurt", "RB Leipzig", "VfB Stuttgart"],
  },
  {
    league: "Ligue 1",
    leaguePath: "/leagues/ligue-1",
    clubs: ["AS Monaco", "Lille", "Lyon", "Marseille", "Nice", "Paris Saint-Germain"],
  },
];

export default function ClubsIndex() {
  const total = CLUB_GROUPS.reduce((n, g) => n + g.clubs.length, 0);

  useSEO({
    title: "Football Club Predictions & Betting Tips | Statlign",
    description:
      "Browse Statlign club prediction hubs across Europe's major leagues — form, head-to-head trends and value odds for every upcoming fixture.",
    canonicalPath: CANONICAL,
    keywords:
      "club predictions, football team betting tips, team form analysis, club odds, team predictions today",
    jsonLd: [
      organizationJsonLd(),
      webPageJsonLd({
        name: "Football Club Predictions",
        description: "Directory of Statlign club prediction hubs across Europe's major leagues.",
        path: CANONICAL,
      }),
      breadcrumbJsonLd(crumbs),
    ],
  });

  return (
    <div className="space-y-8 sm:space-y-10 animate-fade-in">
      <Breadcrumbs items={crumbs} className="text-sm text-muted-foreground" />

      <header className="space-y-2">
        <h1 className="flex items-center gap-2 text-2xl sm:text-3xl font-bold">
          <Shield className="h-6 w-6 sm:h-7 sm:w-7 text-primary shrink-0" />
           {uiText("Football Club Predictions")} </h1>
        <p className="max-w-3xl text-muted-foreground">
          {total}  {uiText("club hubs covering Europe's major leagues. Each hub collects that club's upcoming fixtures, model probabilities and value odds, weighted by recent form, head-to-head history and player availability.")} </p>
      </header>

      {CLUB_GROUPS.map((group) => (
        <section key={group.league} className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">{group.league}  {uiText("Clubs")}</h2>
            <Link to={group.leaguePath} className="text-sm font-medium text-primary hover:underline">
               {uiText("League hub")} </Link>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {group.clubs.map((club) => (
              <Card key={club} className="flex h-full flex-col p-4">
                <h3 className="text-sm font-semibold">{club}  {uiText("Predictions")}</h3>
                <p className="mb-3 mt-1 text-xs text-muted-foreground">
                   {uiText("Upcoming")} {club}  {uiText("fixtures, model probabilities and value odds.")} </p>
                <Link
                  to={`/clubs/${slugify(club)}`}
                  className="group mt-auto inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                >
                   {uiText("View")} {club}  {uiText("hub")} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </Card>
            ))}
          </div>
        </section>
      ))}

      <InternalLinks />

      <LegalDisclaimer />
    </div>
  );
}
